/**
 * Réglages : coordonnées, SIRET, horaires, réseaux.
 *
 * Ces valeurs ne restent pas dans l'admin. Elles partent dans le JSON-LD de
 * **toutes** les pages (`localBusinessJsonLd` : `telephone`, `identifier`,
 * `address`, `openingHoursSpecification`), dans `llms.txt`, et dans les
 * mentions légales. Une valeur fausse ici n'est pas une case mal remplie :
 * c'est une fausse coordonnée publiée sous l'identité de l'entreprise, reprise
 * telle quelle par les moteurs et les assistants.
 *
 * D'où le parti pris de l'écran : **les placeholders sont signalés, pas
 * tolérés**. Le téléphone fictif des premières versions (`+33600000000`) et le
 * SIRET vide sont ceux du jeu de départ ; tant qu'ils n'ont pas été remplacés,
 * l'interface le dit en haut de page et sur le champ lui-même. Aucune valeur d'entreprise n'est
 * proposée ni pré-remplie : ce que l'admin ignore, elle laisse vide.
 */
import ErrorOutlinedIcon from "@mui/icons-material/ErrorOutlined";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import InputAdornment from "@mui/material/InputAdornment";
import Paper from "@mui/material/Paper";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useEffect, useState } from "react";
import { erreurChamp } from "../composants/ContenuAtelier";
import {
  type Reglages as ValeursReglages,
  useEnregistrerReglages,
  useReglages,
} from "../composants/ContenuDonnees";

/** Numéro provisoire posé par le jeu de données initial. Il n'appartient à personne. */
const TELEPHONE_PLACEHOLDER = "+33600000000";

/**
 * Format lu par `horairesJsonLd` : « Lu-Ve 08:00-18:00 ». Toute autre écriture
 * est acceptée en base mais ne produit aucun `openingHoursSpecification` — les
 * horaires disparaissent alors des données structurées sans le moindre message.
 */
const FORMAT_HORAIRES = /^\s*[a-zé]{2}\s*-\s*[a-zé]{2}\s+\d{1,2}:\d{2}\s*-\s*\d{1,2}:\d{2}\s*$/i;

type Champ = {
  cle: string;
  libelle: string;
  aide?: string;
  multiligne?: boolean;
  prefixe?: string;
};

type Groupe = {
  titre: string;
  description?: string;
  champs: Champ[];
};

const GROUPES: Groupe[] = [
  {
    titre: "Identité",
    champs: [
      { cle: "entreprise_nom", libelle: "Nom de l'entreprise" },
      { cle: "entreprise_baseline", libelle: "Baseline" },
    ],
  },
  {
    titre: "Coordonnées",
    description:
      "Reprises dans le pied de page, la page contact, le JSON-LD de chaque page et llms.txt.",
    champs: [
      { cle: "entreprise_adresse", libelle: "Adresse" },
      { cle: "entreprise_code_postal", libelle: "Code postal" },
      { cle: "entreprise_ville", libelle: "Ville" },
      {
        cle: "entreprise_telephone",
        libelle: "Téléphone",
        aide: "Au format international, par exemple +33 suivi du numéro sans le zéro initial.",
      },
      {
        cle: "entreprise_telephone_2",
        libelle: "Second téléphone",
        aide: "Même format. Affiché en complément sur la page contact, le pied de page et les mentions légales ; laissez vide s'il n'y en a qu'un.",
      },
      { cle: "entreprise_email", libelle: "Email de contact" },
    ],
  },
  {
    titre: "Identification légale",
    description: "Publiée dans les mentions légales, dans llms.txt et comme identifiant JSON-LD.",
    champs: [
      {
        cle: "entreprise_siret",
        libelle: "SIRET",
        aide: "Quatorze chiffres. Tant qu'il est vide, les mentions légales affichent « à compléter ».",
      },
    ],
  },
  {
    titre: "Position et horaires",
    description:
      "La position alimente le bloc géographique des données structurées ; les horaires n'en produisent que s'ils suivent exactement le format attendu.",
    champs: [
      { cle: "entreprise_lat", libelle: "Latitude" },
      { cle: "entreprise_lng", libelle: "Longitude" },
      {
        cle: "horaires",
        libelle: "Horaires d'ouverture",
        aide: "Format attendu : « Lu-Ve 08:00-18:00 ».",
      },
    ],
  },
  {
    titre: "Réseaux sociaux",
    description:
      "Enregistrés ici pour être disponibles, mais le site ne les publie pas encore : aucun lien ni aucun « sameAs » n'est généré à ce jour.",
    champs: [
      { cle: "reseau_facebook", libelle: "Facebook", prefixe: "https://" },
      { cle: "reseau_instagram", libelle: "Instagram", prefixe: "https://" },
      { cle: "reseau_linkedin", libelle: "LinkedIn", prefixe: "https://" },
    ],
  },
];

const TOUTES_LES_CLES = GROUPES.flatMap((groupe) => groupe.champs.map((champ) => champ.cle));

type Anomalie = {
  cle: string;
  message: string;
  /** `true` quand la valeur publiée est fausse, et non simplement absente. */
  fausse: boolean;
};

/**
 * Ce qui est publié à tort. Un champ vide n'est pas traité comme un mensonge —
 * la landing sait taire ce qu'elle ignore — sauf pour le SIRET, que les
 * mentions légales sont tenues d'afficher.
 */
export function anomaliesReglages(valeurs: ValeursReglages): Anomalie[] {
  const anomalies: Anomalie[] = [];
  const valeur = (cle: string) => (valeurs[cle] ?? "").trim();

  if (valeur("entreprise_telephone") === TELEPHONE_PLACEHOLDER) {
    anomalies.push({
      cle: "entreprise_telephone",
      fausse: true,
      message:
        "Ce numéro est celui du jeu de données de départ. Il est publié tel quel dans le JSON-LD de chaque page, dans llms.txt et sur la page contact : un visiteur qui l'appelle ne joint personne.",
    });
  }

  if (valeur("entreprise_siret") === "") {
    anomalies.push({
      cle: "entreprise_siret",
      fausse: false,
      message:
        "Aucun SIRET n'est renseigné. Les mentions légales affichent « à compléter » et llms.txt indique « non communiqué à ce jour » — mention obligatoire pour une entreprise qui démarche des particuliers.",
    });
  }

  const horaires = valeur("horaires");
  if (horaires !== "" && !FORMAT_HORAIRES.test(horaires)) {
    anomalies.push({
      cle: "horaires",
      fausse: false,
      message:
        "Ces horaires ne suivent pas le format « Lu-Ve 08:00-18:00 » : ils resteront affichés sur le site, mais disparaîtront des données structurées, où ils ne seront tout simplement pas déclarés.",
    });
  }

  const email = valeur("entreprise_email");
  if (email !== "" && !email.includes("@")) {
    anomalies.push({
      cle: "entreprise_email",
      fausse: true,
      message: "Cette adresse n'en est pas une : elle est pourtant publiée comme telle.",
    });
  }

  return anomalies;
}

export function Reglages() {
  const reglages = useReglages();
  const enregistrement = useEnregistrerReglages();

  // Le brouillon n'est alimenté qu'une fois : un rafraîchissement en arrière-plan
  // ne doit pas écraser une saisie en cours.
  const [reference, setReference] = useState<ValeursReglages | null>(null);
  const [brouillon, setBrouillon] = useState<ValeursReglages | null>(null);
  const [succes, setSucces] = useState(false);

  useEffect(() => {
    if (reglages.data === undefined || reference !== null) return;
    const valeurs: ValeursReglages = {};
    for (const cle of TOUTES_LES_CLES) valeurs[cle] = reglages.data[cle] ?? "";
    setReference(valeurs);
    setBrouillon(valeurs);
  }, [reglages.data, reference]);

  if (reglages.isPending || brouillon === null || reference === null) {
    return <Skeleton variant="rounded" height={400} />;
  }

  if (reglages.isError) {
    return <Alert severity="error">Réglages illisibles : {reglages.error.messageGlobal}</Alert>;
  }

  const anomalies = anomaliesReglages(brouillon);
  const anomaliesParCle = new Map(anomalies.map((anomalie) => [anomalie.cle, anomalie]));

  const modifications: ValeursReglages = {};
  for (const cle of TOUTES_LES_CLES) {
    const valeur = brouillon[cle] ?? "";
    if (valeur !== (reference[cle] ?? "")) modifications[cle] = valeur;
  }
  const modifie = Object.keys(modifications).length > 0;

  function modifier(cle: string, valeur: string) {
    // `null` ne peut pas survenir ici (le rendu s'arrête plus haut tant que le
    // brouillon n'est pas chargé) ; l'étaler produirait un objet vide, donc la
    // perte de tous les autres réglages. On le laisse passer intact.
    setBrouillon((actuel) => (actuel === null ? actuel : { ...actuel, [cle]: valeur }));
    setSucces(false);
  }

  function enregistrer() {
    if (!modifie) return;
    enregistrement.mutate(modifications, {
      onSuccess: (valeurs) => {
        const majour: ValeursReglages = {};
        for (const cle of TOUTES_LES_CLES) majour[cle] = valeurs[cle] ?? "";
        setReference(majour);
        setBrouillon(majour);
        setSucces(true);
      },
    });
  }

  const fausses = anomalies.filter((anomalie) => anomalie.fausse);

  return (
    <Stack spacing={3}>
      <Typography variant="body1" color="text.secondary">
        Ces valeurs alimentent le pied de page, la page contact, les mentions légales, le JSON-LD de
        chaque page et llms.txt. Elles ne restent pas dans l'administration.
      </Typography>

      {anomalies.length > 0 && (
        <Alert severity={fausses.length > 0 ? "error" : "warning"} icon={<ErrorOutlinedIcon />}>
          <AlertTitle>
            {fausses.length > 0
              ? "Le site publie actuellement des informations fausses"
              : "Informations manquantes ou inexploitables"}
          </AlertTitle>
          <Stack component="ul" spacing={0.5} sx={{ pl: 2, m: 0 }}>
            {anomalies.map((anomalie) => (
              <Typography component="li" variant="body2" key={anomalie.cle}>
                <strong>{libelleDe(anomalie.cle)} :</strong> {anomalie.message}
              </Typography>
            ))}
          </Stack>
        </Alert>
      )}

      {anomalies.length === 0 && (
        <Alert severity="success">
          Aucun placeholder ne subsiste : les coordonnées publiées sont celles renseignées ici.
        </Alert>
      )}

      {GROUPES.map((groupe) => (
        <Paper key={groupe.titre} variant="outlined" sx={{ p: { xs: 2, md: 3 } }}>
          <Typography variant="h4" component="h2" gutterBottom>
            {groupe.titre}
          </Typography>
          {groupe.description && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {groupe.description}
            </Typography>
          )}

          <Box
            sx={{
              display: "grid",
              gap: 2.5,
              gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" },
            }}
          >
            {groupe.champs.map((champ) => {
              const anomalie = anomaliesParCle.get(champ.cle);
              const erreurApi = erreurChamp(enregistrement.error, champ.cle);

              return (
                <TextField
                  key={champ.cle}
                  fullWidth
                  multiline={champ.multiligne}
                  label={champ.libelle}
                  value={brouillon?.[champ.cle] ?? ""}
                  onChange={(evenement) => modifier(champ.cle, evenement.target.value)}
                  error={erreurApi !== undefined || anomalie?.fausse === true}
                  color={anomalie !== undefined && !anomalie.fausse ? "warning" : undefined}
                  focused={anomalie !== undefined ? true : undefined}
                  helperText={erreurApi ?? anomalie?.message ?? champ.aide}
                  slotProps={
                    champ.prefixe
                      ? {
                          input: {
                            startAdornment: (
                              <InputAdornment position="start">{champ.prefixe}</InputAdornment>
                            ),
                          },
                        }
                      : undefined
                  }
                />
              );
            })}
          </Box>
        </Paper>
      ))}

      {enregistrement.error && (
        <Alert severity="error" role="alert">
          {enregistrement.error.messageGlobal}
        </Alert>
      )}

      {succes && !modifie && (
        <Alert severity="success" aria-live="polite">
          Réglages enregistrés. Ils seront repris sur le site à la prochaine publication.
        </Alert>
      )}

      <Box>
        <Button
          variant="contained"
          disabled={!modifie || enregistrement.isPending}
          onClick={enregistrer}
          startIcon={
            enregistrement.isPending ? <CircularProgress size={16} color="inherit" /> : undefined
          }
        >
          {enregistrement.isPending ? "Enregistrement…" : "Enregistrer les réglages"}
        </Button>
      </Box>
    </Stack>
  );
}

function libelleDe(cle: string): string {
  for (const groupe of GROUPES) {
    const champ = groupe.champs.find((candidat) => candidat.cle === cle);
    if (champ) return champ.libelle;
  }
  return cle;
}
